import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Button, Input } from "reactstrap";
import TextField from "../../../common/TextField";
import { toast } from "react-toastify";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import {
  getTrainingDocByIdService,
  updateTrainingDocService,
} from "../../../../Services/CRMAdminServices/CRMTrainingDocsService";

export default function EditTrainingDoc() {
  const queryClient = useQueryClient();
  const navigate = useSafeNavigate();
  const { id } = useParams<{ id: string }>();

  const [formData, setFormData] = useState<{
    documentName: string;
    documentFileName: string;
    file: File | null;
  }>({
    documentName: "",
    documentFileName: "",
    file: null,
  });

  const [formErrors, setFormErrors] = useState<any>({});

  // Fetch existing document by ID
  const { data, isLoading } = useGetRequestQuery(
    ["getTrainingDocById", id],
    () => getTrainingDocByIdService(id),
    { enabled: !!id }
  );

  // Prefill form when data loads

  useEffect(() => {
  if (data && !isLoading) {
    const doc = data?.data ?? data; // handle both cases
    setFormData({
      documentName: doc.documentName || "",
      documentFileName: doc.documentFileName || "",
      file: null,
    });
  }
}, [data, isLoading]);
//   useEffect(() => {
//     if (data && !isLoading) {
//       setFormData({
//         documentName: data.documentName,
//         file: null,
//       });
//     }
//   }, [data, isLoading]);

  const handleValueChange = (name: string, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev: any) => ({ ...prev, [name]: "" }));
  };

  // Mutation for update
  const { mutate: updateDoc, isPending: isUpdating } = useMutationRequestQuery(
    "updateTrainingDoc",
    updateTrainingDocService,
    {
      onSuccess: () => {
        toast.success("Training Doc updated successfully.");
        navigate("/CRMTraining");
        queryClient.invalidateQueries({ queryKey: ["getAllTrainingDocs"] });
      },
      onError: () => {
        toast.error("Failed to update training doc.");
      },
    }
  );

  // Submit handler
  const handleSubmit = (e: any) => {
    e.preventDefault();

    let errors: any = {};
    if (!formData.documentName)
      errors.documentName = "Document Name is required!";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const formDataObj = new FormData();
    formDataObj.append("id", id!); // lowercase id to match API
    formDataObj.append("documentName", formData.documentName);

   const doc = data?.data ?? data; // unwrap safely

  if (formData.file) {
    formDataObj.append("file", formData.file);
  } else if (doc?.documentFileName) {
    formDataObj.append("documentFileName", doc.documentFileName);
  }

    updateDoc(formDataObj);
  };

  return (
    <div className="px-4 py-4">
      <Form>
        <TextField
          label="Document Name:"
          type="text"
          name="documentName"
          value={formData.documentName}
          setValue={handleValueChange}
          placeholder="Enter document name"
          passCol="col-6"
          error={formErrors.documentName}
          errorMessage="Document Name is required!"
        />

        <div className="mb-3" style={{width:"40%"}}>
          <label className="form-label fw-bold">Replace File (optional):</label>
        {(data?.data?.documentFileName || data?.data?.documentFileName) && (
  <p className="text-muted">
    Current File: {data?.data?.documentFileName ?? data?.data?.documentFileName}
  </p>
)}
          <Input
            type="file"
                accept=".pdf, .doc, .docx, .xls, .xlsx, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

            onChange={(e) =>
              handleValueChange(
                "file",
                e.target.files ? e.target.files[0] : null
              )
            }
          />
        </div>

        <div className="d-flex gap-3 mt-3">
          <Button
            type="button"
            onClick={() => navigate("/CRMTrainingDocs")}
            color="secondary"
          >
            Back to List
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            color="primary"
            disabled={isUpdating}
          >
            {isUpdating ? "Updating..." : "Update Training Doc"}
          </Button>
        </div>
      </Form>
    </div>
  );
}
